// Journal de visite (T-242c) — dictée brute et notes horodatées, CONSERVÉES, en AJOUT SEUL.
// Zone mobile gelée N°861 : composant NOUVEAU, monté par insertion de lignes ; aucune édition ni suppression d'entrée.
// Reprise hors ligne : une entrée non envoyée reste en file locale (identifiant fixe ⇒ le renvoi ne crée pas de doublon).
import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, NotebookPen, WifiOff } from "lucide-react";
import MicButton from "./MicButton";
import { getDalAuthToken } from "./dalState";
import { enteteTenant } from "../../utils/enteteTenant";
import { tenantResolu } from "../../utils/tenantResolu";
import { MAX_TEXTE, ecrireFile, envoyerEntree, fabriquerEntree, fusionner, lireFile } from "./journalVisite";

const API = process.env.REACT_APP_BACKEND_URL;

const carte = { background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)", borderRadius: 12, padding: 16 };
const champ = { background: "var(--oh-bg-surface)", color: "var(--oh-text)", border: "0.5px solid var(--oh-border)", borderRadius: 8, padding: 12,
                minHeight: 96, width: "100%", fontWeight: 400, lineHeight: 1.6 };
const secondaire = { background: "transparent", color: "var(--oh-accent-text)", border: "0.5px solid var(--oh-accent)", borderRadius: 8, minHeight: 44,
                     padding: "0 16px", fontWeight: 500 };
const quand = (iso) => {
  if (!iso) return "en attente d'envoi";
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
};

export default function JournalVisite({ dossierId }) {
  const [serveur, setServeur] = useState([]);
  const [file, setFile] = useState(() => lireFile(dossierId));
  const [etat, setEtat] = useState("chargement");     // chargement | pret | erreur
  const [texte, setTexte] = useState("");
  const [dicte, setDicte] = useState(false);          // le texte courant vient de la dictée
  const [envoi, setEnvoi] = useState(false);
  const fileRef = useRef(file);
  fileRef.current = file;

  const entetes = useCallback(() => {
    const token = getDalAuthToken();
    return { ...enteteTenant(tenantResolu()), ...(token && { Authorization: `Bearer ${token}` }) };
  }, []);

  const charger = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/dal/save-mobile/${dossierId}/journal`, { headers: entetes() });
      if (!res.ok) throw new Error(String(res.status));
      const d = await res.json();
      setServeur(Array.isArray(d.journal) ? d.journal : []);
      setEtat("pret");
    } catch {
      setEtat("erreur");
    }
  }, [dossierId, entetes]);

  const vider = useCallback(async () => {
    let restant = [...fileRef.current];
    for (const e of fileRef.current) {
      const r = await envoyerEntree({ api: API, dossierId, entree: e, entetes: entetes() });
      if (r === "reessayer") break;
      restant = restant.filter(x => x.entry_id !== e.entry_id);      // "ok" ou "refuse" : on ne boucle pas
    }
    ecrireFile(dossierId, restant);
    setFile(restant);
    return restant;
  }, [dossierId, entetes]);

  useEffect(() => {
    (async () => { if (fileRef.current.length) await vider(); await charger(); })();
    const retour = async () => { if (fileRef.current.length) { await vider(); await charger(); } };
    window.addEventListener("online", retour);
    return () => window.removeEventListener("online", retour);
  }, [vider, charger]);

  const ajouter = async () => {
    const entree = fabriquerEntree(texte, dicte ? "dictee" : "note");
    if (!entree || envoi) return;
    setEnvoi(true);
    const suite = [...fileRef.current, entree];
    ecrireFile(dossierId, suite);                                    // d'abord en file : rien ne se perd si le réseau tombe
    fileRef.current = suite;                                         // vider() lit la ref : elle ne se met à jour qu'au rendu suivant
    setFile(suite);
    setTexte(""); setDicte(false);
    await vider();
    await charger();
    setEnvoi(false);
  };

  const entrees = fusionner(serveur, file);
  return (
    <section style={carte} data-testid="journal-visite" aria-label="Journal de visite">
      <div className="flex items-center gap-2 mb-1" style={{ color: "var(--oh-text)", fontWeight: 500 }}>
        <NotebookPen className="w-4 h-4" aria-hidden="true" /> Journal de visite
      </div>
      <p className="text-xs mb-3" style={{ color: "var(--oh-text-secondary)" }}>
        Notes et dictées horodatées, conservées telles quelles. Elles servent de source à la rédaction du DAL.
      </p>

      <div className="flex items-center justify-between gap-2 mb-2">
        <label htmlFor="journal-visite-texte" className="text-xs" style={{ color: "var(--oh-text-secondary)", fontWeight: 500 }}>Nouvelle note</label>
        <MicButton value={texte} onTranscript={(v, o) => { setTexte(v); if (o?.fin) setDicte(true); }} />
      </div>
      <textarea id="journal-visite-texte" value={texte} maxLength={MAX_TEXTE} style={champ} data-testid="journal-visite-texte"
        onChange={e => { setTexte(e.target.value); if (!e.target.value) setDicte(false); }}
        placeholder="Ex : escalier raide, rampe d'un seul côté, deux chutes cette année…" />
      <div className="flex items-center justify-between gap-2 mt-2">
        <span className="text-xs" style={{ color: "var(--oh-text-muted)" }}>{texte.length}/{MAX_TEXTE}</span>
        <button type="button" onClick={ajouter} disabled={!texte.trim() || envoi} style={{ ...secondaire, opacity: !texte.trim() || envoi ? 0.5 : 1 }}
          data-testid="journal-visite-ajouter">
          {envoi ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" aria-hidden="true" /> : null}Ajouter au journal
        </button>
      </div>

      {file.length > 0 && (
        <div role="status" className="flex items-center gap-2 mt-3 text-xs px-3 py-2" data-testid="journal-visite-file"
          style={{ background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)", borderRadius: 8 }}>
          <WifiOff className="w-3 h-3" aria-hidden="true" />
          {file.length} {file.length > 1 ? "notes seront envoyées" : "note sera envoyée"} dès que le réseau revient.
        </div>
      )}

      <div className="mt-4">
        {etat === "chargement" && (
          <div className="flex items-center gap-2 text-xs" style={{ color: "var(--oh-text-secondary)" }} data-testid="journal-visite-chargement">
            <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" /> Chargement du journal…
          </div>
        )}
        {etat === "erreur" && (
          <div role="alert" className="flex items-center justify-between gap-2 text-xs px-3 py-2" data-testid="journal-visite-erreur"
            style={{ background: "var(--oh-error-bg)", color: "var(--oh-error-text)", border: "0.5px solid var(--oh-error-border)", borderRadius: 8 }}>
            <span>Le journal n'a pas pu être chargé. Vos nouvelles notes restent enregistrées sur l'appareil.</span>
            <button type="button" onClick={charger} className="underline" style={{ minHeight: 44 }}>Réessayer</button>
          </div>
        )}
        {etat === "pret" && entrees.length === 0 && (
          <p className="text-xs" style={{ color: "var(--oh-text-secondary)" }} data-testid="journal-visite-vide">
            Le journal est vide. Dictez ou saisissez votre première note.
          </p>
        )}
        {entrees.length > 0 && (
          <ul className="space-y-2" data-testid="journal-visite-liste">
            {[...entrees].reverse().map(e => (
              <li key={e.id} className="text-sm" data-testid="journal-visite-entree"
                style={{ background: "var(--oh-bg-surface)", border: "0.5px solid var(--oh-border)", borderRadius: 8, padding: "8px 12px", color: "var(--oh-text)" }}>
                <div className="text-xs mb-1" style={{ color: "var(--oh-text-muted)" }}>
                  {e.type === "dictee" ? "Dictée" : "Note"} · {quand(e.horodatage || null)}
                </div>
                <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{e.texte}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
